import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  CircularProgress,
  FormHelperText
} from '@mui/material';
import { AuthContext } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  Timestamp
} from 'firebase/firestore';
import { 
  TARGET_TYPES,
  GOAL_STATUSES,
  MONTHS,
  QUARTERS,
  generatePeriodDates
} from '../../constants/goalsConstants';

const GoalForm = ({ initialData = null, onSubmit, onCancel, employees }) => {
  const { currentUser, isAdmin } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);
  
  // Stan formularza
  const [formData, setFormData] = useState({
    year: currentYear,
    month: null,
    quarter: null,
    periodType: 'monthly', // 'monthly', 'quarterly', 'yearly'
    targetValue: '',
    targetType: 'sales_value',
    assignedTo: null, // null oznacza cel firmowy
    assignedTeam: null,
    notes: '',
    status: 'in_progress'
  });

  // Jeśli mamy dane początkowe (edycja), załaduj je
  useEffect(() => {
    if (initialData) {
      const periodType = initialData.month 
        ? 'monthly' 
        : initialData.quarter 
          ? 'quarterly' 
          : 'yearly';
      
      setFormData({
        year: initialData.year || currentYear,
        month: initialData.month || null,
        quarter: initialData.quarter || null,
        periodType,
        targetValue: initialData.targetValue || '',
        targetType: initialData.targetType || 'sales_value',
        assignedTo: initialData.assignedTo || null,
        assignedTeam: initialData.assignedTeam || null,
        notes: initialData.notes || '',
        status: initialData.status || 'in_progress'
      });
    }
  }, [initialData, currentYear]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Specjalna obsługa dla pola periodType
    if (name === 'periodType') {
      const newFormData = {
        ...formData,
        periodType: value,
        month: value === 'monthly' ? (new Date().getMonth() + 1) : null,
        quarter: value === 'quarterly' ? Math.ceil((new Date().getMonth() + 1) / 3) : null
      };
      setFormData(newFormData);
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    
    // Wyczyść błąd dla tego pola
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.year) {
      newErrors.year = 'Rok jest wymagany';
    }
    
    if (formData.periodType === 'monthly' && !formData.month) {
      newErrors.month = 'Miesiąc jest wymagany dla celu miesięcznego';
    }
    
    if (formData.periodType === 'quarterly' && !formData.quarter) {
      newErrors.quarter = 'Kwartał jest wymagany dla celu kwartalnego';
    }
    
    if (!formData.targetValue || formData.targetValue <= 0) {
      newErrors.targetValue = 'Wartość docelowa musi być większa od zera';
    }
    
    if (!formData.targetType) {
      newErrors.targetType = 'Typ celu jest wymagany';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Generuj daty początku i końca okresu
      const { startDate, endDate } = generatePeriodDates(
        formData.year,
        formData.periodType === 'monthly' ? formData.month : null,
        formData.periodType === 'quarterly' ? formData.quarter : null
      );
      
      // Przygotuj dane do zapisania
      const goalData = {
        year: formData.year,
        month: formData.periodType === 'monthly' ? formData.month : null,
        quarter: formData.periodType === 'quarterly' ? formData.quarter : null,
        targetValue: Number(formData.targetValue),
        targetType: formData.targetType,
        assignedTo: formData.assignedTo,
        assignedTeam: formData.assignedTeam,
        currentProgress: initialData?.currentProgress || 0,
        status: formData.status,
        startDate: Timestamp.fromDate(startDate),
        endDate: Timestamp.fromDate(endDate),
        notes: formData.notes,
        createdAt: initialData?.createdAt || Timestamp.now(),
        lastUpdate: Timestamp.now()
      };
      
      let result;
      
      if (initialData) {
        // Aktualizacja istniejącego celu
        const goalRef = doc(db, 'goals', initialData.id);
        await updateDoc(goalRef, goalData);
        result = { id: initialData.id, ...goalData };
      } else {
        // Dodanie nowego celu
        const goalsRef = collection(db, 'goals');
        const docRef = await addDoc(goalsRef, goalData);
        result = { id: docRef.id, ...goalData };
      }
      
      // Wywołaj callback z danymi
      if (onSubmit) {
        onSubmit(result);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Błąd podczas zapisywania celu:', error);
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        {/* Typ okresu */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Typ okresu</InputLabel>
            <Select
              name="periodType"
              value={formData.periodType}
              onChange={handleInputChange}
              label="Typ okresu"
            >
              <MenuItem value="monthly">Miesięczny</MenuItem>
              <MenuItem value="quarterly">Kwartalny</MenuItem>
              <MenuItem value="yearly">Roczny</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Rok */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={!!errors.year}>
            <InputLabel>Rok</InputLabel>
            <Select
              name="year"
              value={formData.year}
              onChange={handleInputChange}
              label="Rok"
            >
              {years.map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
            {errors.year && <FormHelperText>{errors.year}</FormHelperText>}
          </FormControl>
        </Grid>

        {/* Miesiąc (tylko dla celów miesięcznych) */}
        {formData.periodType === 'monthly' && (
          <Grid item xs={12} md={4}>
            <FormControl fullWidth error={!!errors.month}>
              <InputLabel>Miesiąc</InputLabel>
              <Select
                name="month"
                value={formData.month || ''}
                onChange={handleInputChange}
                label="Miesiąc"
              >
                {Object.entries(MONTHS).map(([key, value]) => (
                  <MenuItem key={key} value={parseInt(key)}>{value}</MenuItem>
                ))}
              </Select>
              {errors.month && <FormHelperText>{errors.month}</FormHelperText>}
            </FormControl>
          </Grid>
        )}

        {/* Kwartał (tylko dla celów kwartalnych) */}
        {formData.periodType === 'quarterly' && (
          <Grid item xs={12} md={4}>
            <FormControl fullWidth error={!!errors.quarter}>
              <InputLabel>Kwartał</InputLabel>
              <Select
                name="quarter"
                value={formData.quarter || ''}
                onChange={handleInputChange}
                label="Kwartał"
              >
                {Object.entries(QUARTERS).map(([key, value]) => (
                  <MenuItem key={key} value={parseInt(key)}>{value}</MenuItem>
                ))}
              </Select>
              {errors.quarter && <FormHelperText>{errors.quarter}</FormHelperText>}
            </FormControl>
          </Grid>
        )}

        {/* Typ celu */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={!!errors.targetType}>
            <InputLabel>Typ celu</InputLabel>
            <Select
              name="targetType"
              value={formData.targetType}
              onChange={handleInputChange}
              label="Typ celu"
            >
              {Object.entries(TARGET_TYPES).map(([key, value]) => (
                <MenuItem key={key} value={key}>{value}</MenuItem>
              ))}
            </Select>
            {errors.targetType && <FormHelperText>{errors.targetType}</FormHelperText>}
          </FormControl>
        </Grid>

        {/* Wartość docelowa */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Wartość docelowa"
            name="targetValue"
            type="number"
            value={formData.targetValue}
            onChange={handleInputChange}
            error={!!errors.targetValue}
            helperText={errors.targetValue}
            InputProps={{
              inputProps: { min: 0 }
            }}
          />
        </Grid>

        {/* Przypisane do */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Przypisane do</InputLabel>
            <Select
              name="assignedTo"
              value={formData.assignedTo || ''}
              onChange={handleInputChange}
              label="Przypisane do"
            >
              <MenuItem value="">Cała firma</MenuItem>
              {employees.map(employee => (
                <MenuItem key={employee.id} value={employee.id}>
                  {`${employee.firstName || ''} ${employee.lastName || ''}`.trim() || employee.id}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Pozostaw puste dla celu ogólnofirmowego
            </FormHelperText>
          </FormControl>
        </Grid>

        {/* Status (tylko dla edycji) */}
        {initialData && (
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                label="Status"
              >
                {Object.entries(GOAL_STATUSES).map(([key, value]) => (
                  <MenuItem key={key} value={key}>{value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* Notatki */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Notatki"
            name="notes"
            multiline
            rows={4}
            value={formData.notes}
            onChange={handleInputChange}
            placeholder="Dodatkowe informacje o celu..."
          />
        </Grid>

        {/* Przyciski */}
        <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={onCancel}
            disabled={loading}
          >
            Anuluj
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : initialData ? 'Zapisz zmiany' : 'Dodaj cel'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GoalForm;
